import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { MATIERES, getMatiereInfo, formatRelativeTime } from "./constants";
import { listResources } from "../../lib/api";
import {
  ChevronRight,
  FileText,
  ExternalLink,
} from "lucide-react";

function MatiereSection({ matiere, onViewAll }) {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const info = getMatiereInfo(matiere.value);
  const Icon = info.icon;

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const { data } = await listResources({ matiere: matiere.value, limit: 4 });
        setResources(data);
      } catch (err) {
        console.error("Error fetching resources:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [matiere.value]);

  const handleNavigateToModule = () => {
    const moduleRoutes = {
      lecture: "/lecture",
      grammaire: "/grammaire",
      dictee: "/dictation",
      math: "/math",
      anglais: "/english",
    };
    const route = moduleRoutes[matiere.value];
    if (route) navigate(route);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className={`${info.lightColor} py-3 px-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${info.color} p-2 rounded-lg`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <CardTitle className="text-base font-bold">{matiere.label}</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {loading ? "..." : resources.length}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={handleNavigateToModule}
            >
              Module
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3">
        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucune ressource</p>
          </div>
        ) : (
          <div className="space-y-2">
            {resources.map((resource) => (
              <div
                key={resource.id}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer group transition-colors"
                onClick={() => navigate(resource.resource_type === 'fiche' || resource.elements ? `/create/${resource.id}` : `/editor/${resource.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {resource.thumbnail ? (
                      <img
                        src={resource.thumbnail}
                        alt=""
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <FileText className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{resource.title}</p>
                    <p className="text-xs text-slate-400">{formatRelativeTime(resource.updated_at)}</p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
        
        {resources.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-indigo-600"
            onClick={() => onViewAll(matiere.value)}
          >
            Voir toutes les ressources
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function TeacherSubjectsTab() {
  const navigate = useNavigate();

  const handleViewAll = (matiere) => {
    // Navigate to ressources tab with filter
    // For now, just log
    console.log("View all for", matiere);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Matieres</h2>
        <p className="text-slate-500 text-sm">
          Accedez rapidement a vos ressources par matiere
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {MATIERES.filter(m => m.value !== "autre").map((matiere) => (
          <MatiereSection
            key={matiere.value}
            matiere={matiere}
            onViewAll={handleViewAll}
          />
        ))}
      </div>
    </div>
  );
}
